import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import * as Icons from "lucide-react";
import { ArrowRight, ArrowLeft, Stethoscope } from "lucide-react";
import { HeroFade, StaggerGrid, StaggerItem } from "@/lib/motion";
import { useSEO } from "@/hooks/useSEO";

const IconOf = ({ name, className }) => {
  const Ico = Icons[name] || Stethoscope;
  return <Ico className={className} strokeWidth={1.75} />;
};

export default function Departments() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/departments").then(({ data }) => setItems(data)); }, []);

  useSEO({
    title: "Specialised Medical Departments",
    description: "Discover our specialized medical departments equipped with state-of-the-art facilities and headed by expert consultants at Afzal Medical Complex."
  });

  return (
    <div data-testid="departments-page">
      <section className="section bg-red-50/40">
        <div className="container-medi max-w-3xl">
          <HeroFade><div className="overline mb-3">Departments</div></HeroFade>
          <HeroFade delay={0.1}><h1 className="text-4xl sm:text-5xl font-heading font-semibold tracking-tight text-slate-900">Specialised departments, seamless care</h1></HeroFade>
          <HeroFade delay={0.2}><p className="mt-5 text-slate-600 text-lg">Each department is led by consultants and equipped with modern facilities to deliver focused, high-quality care.</p></HeroFade>
        </div>
      </section>

      <section className="section">
        <StaggerGrid className="container-medi grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((d) => (
            <StaggerItem key={d.id}>
              <Link to={`/departments/${d.slug || d.id}`} className="card-medi group block" data-testid={`dept-item-${d.id}`}>
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  {d.image_url && <img src={d.image_url} alt={d.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />}
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-red-50 text-red-700 grid place-items-center">
                      <IconOf name={d.icon} className="w-4 h-4" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-slate-900">{d.name}</h3>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">{d.short_description}</p>
                  <div className="mt-4 text-sm text-red-700 font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">Learn more <ArrowRight className="w-4 h-4" /></div>
                </div>
              </Link>
            </StaggerItem>
          ))}
        </StaggerGrid>
      </section>
    </div>
  );
}

export function DepartmentDetail() {
  const { slug } = useParams();
  const [dept, setDept] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/departments/${slug}`).then(({ data }) => setDept(data)).catch(() => setError("Department not found"));
  }, [slug]);

  useSEO({
    title: dept ? `${dept.name} Department` : "Department Details",
    description: dept ? `${dept.name} department details, services, and team at Afzal Medical Complex. ${dept.short_description}` : "Load Department details...",
    schema: dept ? {
      "@context": "https://schema.org",
      "@type": "MedicalWebPage",
      "name": `${dept.name} Department`,
      "description": dept.short_description,
      "about": {
        "@type": "MedicalSpecialty",
        "name": dept.name
      },
      "provider": {
        "@type": "MedicalOrganization",
        "name": "Afzal Medical Complex",
        "url": "https://afzalmedicalcomplex.com"
      }
    } : null
  });

  if (error) return <div className="container-medi section" data-testid="dept-not-found">{error}</div>;
  if (!dept) return <div className="container-medi section text-slate-500">Loading…</div>;

  return (
    <div data-testid="dept-detail-page">
      <section className="section bg-red-50/40">
        <div className="container-medi">
          <Link to="/departments" className="text-sm text-red-700 inline-flex items-center gap-1 mb-6"><ArrowLeft className="w-4 h-4" /> All departments</Link>
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-xl bg-red-600 text-white grid place-items-center"><IconOf name={dept.icon} className="w-5 h-5" /></div>
                <div className="overline">Department</div>
              </div>
              <h1 className="text-4xl sm:text-5xl font-heading font-semibold text-slate-900">{dept.name}</h1>
              <p className="mt-5 text-slate-600 text-lg leading-relaxed">{dept.description}</p>
            </div>
            {dept.image_url && <img src={dept.image_url} alt={dept.name} className="rounded-3xl shadow-medi-lg w-full aspect-[4/3] object-cover" />}
          </div>
        </div>
      </section>

      {dept.services_list?.length > 0 && (
        <section className="section">
          <div className="container-medi">
            <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-6">Services offered</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {dept.services_list.map((s) => (
                <div key={s} className="card-medi p-5 flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-slate-700">{s}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
